import { Ride, ShareIcon, UserService } from '@geovelo-frontends/commons';
import { Box, Skeleton, Typography } from '@mui/material';
import { Link, navigate } from 'gatsby';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '../../components/button';
import { FavoriteCheckedIcon, FavoriteIcon } from '../../components/icons';
import ShareDialog from '../../components/share-dialog';
import { AppContext } from '../../context';
import { TPageState } from '../../page-props';

function Header({
  path,
  ride,
  description,
}: {
  description: string;
  path: string;
  ride: Ride | undefined;
}): React.JSX.Element {
  const [updatingFavorites, setUpdatingFavorites] = useState(false);
  const [shareDialogOpen, openShareDialog] = useState(false);
  const {
    user: { current: currentUser, rides: userRides },
    actions: { getUserRides, setUserRides },
  } = useContext(AppContext);
  const {
    t,
    i18n: { language: currentLanguage },
  } = useTranslation();

  async function handleAddToFavorites() {
    if (!ride || typeof ride.id !== 'number' || !currentUser || !userRides) return;

    setUpdatingFavorites(true);

    try {
      const newUserRide = await UserService.addRide(ride.id);
      if (newUserRide) getUserRides();

      const index = userRides.findIndex(({ rideId }) => rideId === ride.id);
      if (newUserRide && index === undefined) setUserRides([...userRides, newUserRide]);
    } catch (err) {
      console.error(err);
    }

    setUpdatingFavorites(false);
  }

  async function handleRemoveFromFavorites() {
    if (!ride || !userRides || typeof ride.id !== 'number') return;

    setUpdatingFavorites(true);

    try {
      await UserService.removeRide(ride.id);
      const index = userRides.findIndex(({ rideId }) => rideId === ride.id);
      if (index !== undefined) {
        const newRides = [...userRides];
        newRides.splice(index, 1);

        setUserRides(newRides);
      }
    } catch (err) {
      console.error(err);
    }

    setUpdatingFavorites(false);
  }

  const isFavorite = (ride && userRides?.find(({ rideId }) => rideId === ride.id)) || false;

  return (
    <>
      <Box
        alignItems={{ xs: 'flex-start', md: 'center' }}
        display="flex"
        flexDirection={{ xs: 'column', md: 'row' }}
        gap={2}
        marginTop={2}
      >
        <Box flexGrow={1}>
          <Typography component="h1" variant="h5">
            {ride ? ride.title : <Skeleton variant="text" width={300} />}
          </Typography>
        </Box>
        <Box
          alignItems="center"
          alignSelf={{ xs: 'flex-end', md: 'center' }}
          display="flex"
          flexShrink={0}
          gap={2}
        >
          {(!ride || ride.privacy === 'public') && (
            <Button
              color="primary"
              disabled={!ride}
              onClick={() => openShareDialog(true)}
              size="small"
              startIcon={<ShareIcon />}
            >
              {t('commons.actions.share')}
            </Button>
          )}
          {!currentUser ? (
            <Button
              color="primary"
              disabled={currentUser === undefined}
              onClick={() =>
                currentUser === null &&
                navigate(`/${currentLanguage}/sign-in`, { state: { prevPath: path } })
              }
              size="small"
              startIcon={<FavoriteIcon />}
            >
              {t('commons.actions.add_to_favorites')}
            </Button>
          ) : currentUser === null ? (
            <Button<TPageState>
              color="primary"
              component={Link}
              disabled={updatingFavorites}
              size="small"
              startIcon={<FavoriteIcon />}
              state={{ prevPath: path }}
              to={`/${currentLanguage}/sign-in`}
            >
              {t('commons.actions.add_to_favorites')}
            </Button>
          ) : isFavorite ? (
            <Button
              color="primary"
              disabled={updatingFavorites}
              onClick={handleRemoveFromFavorites}
              size="small"
              startIcon={
                <FavoriteCheckedIcon sx={({ palette }) => ({ fill: palette.primary.main })} />
              }
            >
              {t('commons.actions.remove_from_favorites')}
            </Button>
          ) : (
            <Button
              color="primary"
              disabled={updatingFavorites}
              onClick={handleAddToFavorites}
              size="small"
              startIcon={<FavoriteIcon />}
            >
              {t('commons.actions.add_to_favorites')}
            </Button>
          )}
        </Box>
      </Box>
      <ShareDialog
        onClose={() => openShareDialog(false)}
        open={shareDialogOpen}
        shareText={`${ride?.title} | ${description}`}
        url={(typeof window !== 'undefined' && window.location.href) || null}
      />
    </>
  );
}

export default Header;
